import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { useCompanyDateFormat } from "../../../../../../utils/hooks/company";
import { renderSettingsResourceLabel } from "../../../../../../utils/helpers/settings";
import { cn } from "../../../../../../lib/utils";

const statusStyles: Record<string, string> = {
    active: "tw-bg-green-50 tw-text-green-700",
    expired: "tw-bg-red-50 tw-text-red-700",
    pending: "tw-bg-orange-50 tw-text-orange-700",
};


function StatusBadge({ value }: { value: string }) {
    const { t } = useTranslation();
    if (!value) return null;
    return (
        <span
            className={cn(
                "tw-px-3 tw-py-1 tw-rounded-md tw-text-xs tw-font-medium",
                statusStyles[value] ?? "tw-bg-muted tw-text-muted-foreground")
            }>
            {t(value)}
        </span>
    );
}

export const useColumns = () => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const columnHelper = createColumnHelper();

    return useMemo(() => [
        columnHelper.accessor("type", {
            header: t("type"),
            cell: (info) => renderSettingsResourceLabel(info.getValue() as any),
        }),
        columnHelper.accessor("startDate", {
            header: t("start-date"),
            cell: (info) => info.getValue() ? moment(info.getValue() as string).format(dateFormat) : null,
        }),
        columnHelper.accessor("endDate", {
            header: t("end-date"),
            cell: (info) => info.getValue() ? moment(info.getValue() as string).format(dateFormat) : null,
        }),
        columnHelper.accessor("state", {
            header: t("state"),
            cell: (info) => info.getValue() as string,
        }),
        columnHelper.accessor("status", {
            header: t("status"),
            cell: (info) => <StatusBadge value={info.getValue() as string} />,
        }),
    ], [t, dateFormat]);
}